import { initializeBuiltInLibraries } from './builtInLibraries.js';
import { errorMessageOf, installGlobalErrorHandlers, showAppError } from '../ui/errors.js';
import { installEditorTextDragGuards } from '../ui/textDragGuards.js';
import { initPanelActions } from '../ui/panelActions.js';
import { initToolbar, revealViewportToolbar, syncViewportToolbarControls, applyStoredViewportRenderMode } from '../ui/toolbar.js';
// The starter document and its records live once, in the engine's templates/,
// and arrive through the package we already depend on — two checked-in copies
// of one document drift, and these had. Its records are a project seed, which
// its `kv` source reads.
import { defaultTemplateJson, defaultTemplateSeed } from 'fluveria-engine-wasm';
import unitDataSourceUrl from './templates/unit-data.json?url';
import { loadTemplateWithDeps } from './templateLoader.js';
import { createRenderLoopController } from './renderLoop.js';
import { createViewportCanvasController } from './viewportCanvas.js';
import { createViewportOverlayController } from './viewportOverlay.js';
import { initViewportSelection } from './viewportSelection.js';
import { createRenderStatusPresenter } from './renderStatusPresenter.js';
import { observeViewportCanvasFrame } from './viewportFrame.js';
import { getEffectiveViewportResolution } from './viewportResolution.js';
import { syncViewportRulers } from './viewportRulers.js';
import { initLeftPanel } from '../ui/leftPanel.js';
import { initWorkspaceLayout } from '../ui/workspaceLayout.js';
import { initEditorSidebar, type EditorSidebarController } from '../ui/editorSidebar.js';
import { initEditorNavigation } from '../ui/editorNavigation.js';
import { ensureShapeEngineInit, SDF_FONT_LOADED_EVENT } from '../sdf/shapeEngine.js';
import { resetCanvasSceneRendererCache } from '../rendering/canvasSceneRenderer.js';
import { state } from '../state/appState.js';
import type { AppState, ViewportTarget } from '../state/appState.js';
import type { SceneNodeId } from '../scene/sceneState.js';
import { ensureFluveriaEngineInit } from '../scene/engine.js';
import { notifyEditorTargetSynced } from '../editor/editorEvents.js';
import { createHistoryStore } from '../editor/historyState.js';
import { createEditorAppRuntime } from './editorAppRuntime.js';
import { DataRuntimeManager, initDataRuntimePointer } from './dataRuntime.js';
import { EventRuntimeManager } from './eventRuntime.js';
import { createDefaultProviderRegistry, parseKvSeed } from './dataProviders.js';
import { DataSourceCredentialStore } from './dataSourceCredentials.js';
import { DataSourceRuntimeManager } from './dataSourceRuntime.js';
import { QueryRuntimeManager } from './queryRuntime.js';
import { resetPan } from '../ui/viewportState.js';
import { createNodeEditorController } from '../ui/nodeEditor.js';
import { initGraphInteractions } from '../ui/interactions.js';
import { createNodeInteractionViewState } from '../ui/nodeInteractions.js';
import { createConnectionStateController } from '../ui/connectionState.js';
import { createEdgeVisualStateController } from '../ui/edgeVisualState.js';
import { createEdgeHintController } from '../ui/edgeHint.js';
import { createSceneTreeController } from '../ui/sceneTree/setup.js';
import { installDebugApi } from './debugApi.js';
import { recordContextForSceneNode } from '../state/recordContextSchema.js';

async function initBackend(appState: AppState): Promise<void> {
  const webgpuAvailable = !!navigator.gpu;
  const webgpuOpt = document.querySelector('#backend-select option[value="webgpu"]') as HTMLOptionElement | null;

  if (!webgpuAvailable && webgpuOpt) {
    webgpuOpt.style.display = 'none';
  }

  appState.backend = webgpuAvailable ? 'webgpu' : 'canvas';
  (document.getElementById('backend-select') as HTMLSelectElement).value = appState.backend;
}

export async function bootstrapApp(): Promise<void> {
  const historyStore = createHistoryStore();
  const editorAppRuntime = createEditorAppRuntime(state, {
    historyStore,
    notifyEditorTargetSynced,
  });
  const appState = editorAppRuntime.appState;
  const eventRuntime = new EventRuntimeManager();
  const dataRuntime = new DataRuntimeManager(appState, eventRuntime);
  await ensureFluveriaEngineInit();
  // Engine exports are readable only once the wasm module is initialised. The
  // web has no project store: its `kv` sources read the starter's seed, held
  // in memory, as the engine's editor does for a document with no project.
  const projectData = parseKvSeed(defaultTemplateSeed());
  const { registry: dataProviderRegistry, memory: memoryDataProvider } = createDefaultProviderRegistry(projectData);
  const dataSourceCredentials = new DataSourceCredentialStore();
  const dataSourceRuntime = new DataSourceRuntimeManager(
    appState,
    dataRuntime,
    dataProviderRegistry,
    dataSourceCredentials.resolve,
    import.meta.url,
    { 'unit-data.json': unitDataSourceUrl },
  );
  const queryRuntime = new QueryRuntimeManager(eventRuntime);
  const evaluateQueries = (): void => {
    try {
      dataRuntime.setQueryOutputs(queryRuntime.evaluateLibrary(appState.queryGraphs, dataSourceRuntime.storeHandle()));
    } catch (error) {
      console.error('Query Graph evaluation failed', error);
    }
  };
  dataRuntime.setQueryResolver(() => queryRuntime.evaluateLibrary(appState.queryGraphs, dataSourceRuntime.storeHandle()));
  dataSourceRuntime.subscribe(evaluateQueries);
  const viewportCanvas = createViewportCanvasController();
  const viewportOverlay = createViewportOverlayController();
  const statusPresenter = createRenderStatusPresenter();
  let getViewportFocusTarget = (): ViewportTarget | null => null;
  let getSelectedSceneNodeId = (): SceneNodeId | null => null;
  const syncViewportControls = (): void => {
    syncViewportToolbarControls(appState, getViewportFocusTarget);
  };
  const renderLoop = createRenderLoopController({
    appState,
    viewportCanvas,
    overlay: viewportOverlay,
    statusPresenter,
    syncEditor: editorAppRuntime.syncEditor,
    notifyEditorTargetSynced,
    getViewportFocusTarget: () => getViewportFocusTarget(),
    getSelectedSceneNodeId: () => getSelectedSceneNodeId(),
    dataRuntime,
  });
  const scheduleRender = renderLoop.scheduleRender;
  dataRuntime.setScheduleRender(scheduleRender);
  const edgeVisualState = createEdgeVisualStateController();
  const edgeHint = createEdgeHintController();
  const nodeInteractionViewState = createNodeInteractionViewState();
  let renderSceneTree = (): void => {};
  const connectionState = createConnectionStateController({
    getEdges: () => appState.edges,
    resolvePendingConnection: editorAppRuntime.connectionMutations.resolvePendingConnection,
    scheduleRender,
  });
  const updateParams = (): void => {
    renderSceneTree();
  };
  const nodeEditor = createNodeEditorController({
    appState,
    commands: editorAppRuntime.commands,
    graphMutations: editorAppRuntime.graphMutations,
    connectionMutations: editorAppRuntime.connectionMutations,
    connectionState,
    edgeVisualState,
    edgeHint,
    nodeInteractionViewState,
    forceRender: renderLoop.forceRender,
    scheduleRender,
    updateParams,
    commandBatch: editorAppRuntime.commandHistory.commandBatch,
  });

  installGlobalErrorHandlers();
  installEditorTextDragGuards();
  initializeBuiltInLibraries(appState);
  let openLeftPanelTab = (_tabName: string): void => {};
  let renderLeftPanelFavorites = (): void => {};
  let renderExternalInspector = (_host: HTMLElement): boolean => false;
  let editorSidebar: EditorSidebarController | null = null;
  const workspaceLayout = initWorkspaceLayout({
    scheduleRender,
    renderGraph: nodeEditor.renderGraph,
    resetPan,
  });
  const editorNavigation = initEditorNavigation({
    appState,
    syncEditor: editorAppRuntime.syncEditor,
    loadComponentSlot: editorAppRuntime.loadComponentSlot,
    loadLibraryItem: editorAppRuntime.loadLibraryItem,
    nodeEditor,
    renderSceneTree: () => renderSceneTree(),
    openSceneTreePanel: () => openLeftPanelTab('scene'),
    openNodeEditor: workspaceLayout.openNodeEditor,
    scheduleRender,
    getGraphSlotActions: () => editorSidebar,
    syncViewportToolbar: syncViewportControls,
    updateParams,
  });
  editorSidebar = initEditorSidebar({
    appState,
    syncEditor: editorAppRuntime.syncEditor,
    commands: editorAppRuntime.commands,
    openComponentSlotEditor: editorNavigation.openComponentSlotEditor,
    renderSceneTree: () => renderSceneTree(),
    renderEditorNavigation: editorNavigation.render,
  });
  const sceneTree = createSceneTreeController({
    appState,
    commandBatch: editorAppRuntime.commandHistory.commandBatch,
    commands: editorAppRuntime.commands,
    editorNavigation,
    editorSidebar,
    nodeEditor,
    scheduleRender,
    loadComponentSlot: editorAppRuntime.loadComponentSlot,
    syncEditor: editorAppRuntime.syncEditor,
    syncViewportToolbar: syncViewportControls,
    onFavoritesChanged: () => renderLeftPanelFavorites(),
    updateParams,
    renderExternalInspector: host => renderExternalInspector(host),
  });
  renderSceneTree = sceneTree.render;
  initGraphInteractions({
    appState,
    historyStore: editorAppRuntime.historyStore,
    commandBatch: editorAppRuntime.commandHistory.commandBatch,
    commands: editorAppRuntime.commands,
    connectionMutations: editorAppRuntime.connectionMutations,
    connectionState,
    edgeVisualState,
    nodeInteractionViewState,
    nodeEditor,
    renderEditorNavigation: editorNavigation.render,
    renderSceneTree: sceneTree.render,
    updateParams,
    scheduleRender,
    isNodeEditorShortcutActive: workspaceLayout.hasNodeEditorFocus,
  });
  initToolbar({
    appState,
    historyStore: editorAppRuntime.historyStore,
    setEditorGraphMode: editorAppRuntime.setEditorGraphMode,
    clearApp: editorAppRuntime.commands.clearApp,
    syncEditor: editorAppRuntime.syncEditor,
    nodeEditor,
    renderEditorNavigation: editorNavigation.render,
    renderSceneTree: sceneTree.render,
    resetRenderState: renderLoop.resetRenderState,
    updateParams,
    scheduleRender,
    reloadDataSources: () => dataSourceRuntime.connectAll(),
    getViewportFocusTarget: sceneTree.getViewportFocusTarget,
  });
  getViewportFocusTarget = sceneTree.getViewportFocusTarget;
  getSelectedSceneNodeId = sceneTree.getSelectedSceneNodeId;
  initViewportSelection({
    overlay: viewportOverlay,
    getPickContext: renderLoop.getPickContext,
    handleViewportClick: async hit => {
      if (!appState.runtimePreview) {
        sceneTree.handleViewportClick(hit);
        return;
      }
      const scene = hit.ownerComponentId ? appState.componentLibrary[hit.ownerComponentId]?.scene : appState.scene;
      const node = hit.pickedNodeId ? scene?.nodes[hit.pickedNodeId] : undefined;
      const binding = node?.events?.activate;
      if (!binding) {
        const status = document.getElementById('status-right');
        if (status) status.textContent = 'No activate event';
        return;
      }
      const graph = appState.eventGraphs[binding.graph];
      if (!graph) return;
      try {
        eventRuntime.syncSchema(appState.runtimeState);
        // Build the payload from what the event declares: it is validated for
        // an exact key match, so an unasked-for field fails the whole dispatch.
        const declared = eventRuntime.eventFields(binding.graph, graph, binding.event);
        const payload: Record<string, unknown> = {};
        for (const field of declared ?? []) {
          // The only fact activation knows about itself.
          if (field === 'sourceNodeId') payload[field] = node.id;
        }
        const result = eventRuntime.dispatch(
          binding.graph,
          graph,
          binding.event,
          payload,
          // Owner key is the scene path of the node, the same key its behavior
          // and its owner-scoped state already use (dataRuntime passes
          // owner.path). Prefixing it with the component id put event writes
          // under `scene/button` while the behavior read `button`, so owner
          // state never round-tripped. Picking is top-level, so the path of a
          // clickable node is its id.
          node.id,
          recordContextForSceneNode(scene, node.id),
        );
        if (result.changedKeys.length > 0) {
          syncRuntimeMode();
          scheduleRender();
        }
        const effectsBySource = new Map<string, typeof result.effects>();
        for (const effect of result.effects) {
          const effects = effectsBySource.get(effect.source) ?? [];
          effects.push(effect);
          effectsBySource.set(effect.source, effects);
        }
        const status = document.getElementById('status-right');
        if (effectsBySource.size && status) status.textContent = 'Saving data…';
        for (const [source, effects] of effectsBySource) {
          await dataSourceRuntime.mutate(source, effects.map(effect => ({
            kind: 'setField' as const,
            key: effect.key,
            field: effect.field,
            value: effect.value,
          })));
        }
        if (effectsBySource.size && status) status.textContent = 'Data updated';
      } catch (error) {
        const message = errorMessageOf(error);
        const status = document.getElementById('status-right');
        if (status) status.textContent = `Action failed: ${message}`;
        showAppError({ title: 'Prototype action failed', message, source: 'Event Graph / data provider' });
        console.error('Prototype action failed', error);
      }
    },
  });
  const runtimeModeButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-runtime-mode]'));
  const runtimeHint = document.getElementById('runtime-preview-hint');
  const viewportArea = document.getElementById('viewport-area');
  const overlayButton = document.querySelector<HTMLButtonElement>('[data-viewport-view-option="overlay"]');
  const activationCount = (): number => {
    const visited = new Set<string>();
    const countScene = (scene: AppState['scene']): number => Object.values(scene.nodes).reduce((count, node) => {
      const nested = node.kind === 'instance' && !visited.has(node.componentId)
        ? (visited.add(node.componentId), countScene(appState.componentLibrary[node.componentId]?.scene ?? { rootIds: [], nodes: {} }))
        : 0;
      return count + (node.events?.activate ? 1 : 0) + nested;
    }, 0);
    return countScene(appState.scene);
  };
  function syncRuntimeMode(): void {
    for (const button of runtimeModeButtons) {
      const active = button.dataset.runtimeMode === (appState.runtimePreview ? 'prototype' : 'edit');
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    if (runtimeHint) {
      runtimeHint.hidden = !appState.runtimePreview;
      const targets = `${activationCount()} interactive ${activationCount() === 1 ? 'target' : 'targets'}`;
      const changed = eventRuntime.lastChangedKeys;
      const stateCount = Object.keys(eventRuntime.inspectorSnapshot().global).length;
      const runtimeSummary = changed.length > 0
        ? `changed: ${changed.join(', ')}`
        : `${stateCount} runtime ${stateCount === 1 ? 'value' : 'values'}`;
      runtimeHint.textContent = `${targets} · ${runtimeSummary}`;
    }
    if (viewportArea) viewportArea.dataset.runtimePreview = appState.runtimePreview ? 'true' : 'false';
    if (overlayButton) {
      overlayButton.disabled = appState.runtimePreview;
      if (appState.runtimePreview) overlayButton.title = 'Overlay is hidden in Prototype mode';
      else syncViewportControls();
    }
  }
  function setRuntimePreview(enabled: boolean): void {
    const leavingPreview = appState.runtimePreview && !enabled;
    appState.runtimePreview = enabled;
    if (leavingPreview) {
      eventRuntime.syncSchema(appState.runtimeState);
      eventRuntime.reset();
      dataRuntime.reset();
    }
    if (enabled) dataRuntime.resume();
    syncRuntimeMode();
    scheduleRender();
  }
  for (const button of runtimeModeButtons) {
    button.addEventListener('click', () => {
      setRuntimePreview(button.dataset.runtimeMode === 'prototype');
    });
  }
  window.addEventListener('keydown', event => {
    if (event.key === 'Escape' && appState.runtimePreview) setRuntimePreview(false);
  });
  syncRuntimeMode();
  initDataRuntimePointer(
    dataRuntime,
    () => getEffectiveViewportResolution(appState),
    () => appState.runtimePreview,
  );
  const leftPanel = initLeftPanel({
    appState,
    editorNavigation,
    historyStore: editorAppRuntime.historyStore,
    nodeEditor,
    renderSceneTree: sceneTree.render,
    selectSceneFavorite: sceneTree.selectSceneFavorite,
    scheduleRender,
    updateParams,
    commandBatch: editorAppRuntime.commandHistory.commandBatch,
    commands: editorAppRuntime.commands,
    loadRuntimeGraph: editorAppRuntime.loadRuntimeGraph,
    eventRuntime,
    dataRuntime,
    dataSourceRuntime,
    queryRuntime,
    reloadDataSources: async () => { await dataSourceRuntime.connectAll(); },
    openNodeEditor: workspaceLayout.openNodeEditor,
    closeNodeEditor: workspaceLayout.closeNodeEditor,
  });
  openLeftPanelTab = leftPanel.openTab;
  renderLeftPanelFavorites = leftPanel.renderFavorites;
  renderExternalInspector = leftPanel.renderInspector;
  initPanelActions({
    openLeftPanelTab: leftPanel.openTab,
  });
  observeViewportCanvasFrame(() => {
    if (appState.viewportSizeMode === 'fit') {
      scheduleRender();
      return;
    }
    syncViewportRulers(getEffectiveViewportResolution(appState));
  });
  window.addEventListener('app-theme-change', () => scheduleRender());
  window.addEventListener(SDF_FONT_LOADED_EVENT, () => {
    // No GPU teardown here: destroying the device flashes a blank frame, and
    // GPU caches are keyed by shape-compile keys, which change once the font
    // registry gains the font. Only the CPU renderer's dirty-rect snapshots
    // can't see font availability, so drop them explicitly.
    resetCanvasSceneRendererCache();
    scheduleRender();
  });
  installDebugApi({
    appState,
    renderLoop,
    syncViewportControls,
    syncEditor: editorAppRuntime.syncEditor,
    resetPan,
    resetHistory: editorAppRuntime.historyStore.resetHistory,
    renderGraph: nodeEditor.renderGraph,
    updateParams,
    renderSceneTree: sceneTree.render,
    renderEditorNavigation: editorNavigation.render,
    memoryDataProvider,
    dataSourceCredentials,
    dataSourceRuntime,
    dataProviderRegistry,
  });
  await initBackend(appState);
  await ensureShapeEngineInit();
  const defaultTemplate = new TextEncoder().encode(defaultTemplateJson());
  await loadTemplateWithDeps(defaultTemplate, {
    appState,
    resetPan,
    resetHistory: editorAppRuntime.historyStore.resetHistory,
    renderGraph: nodeEditor.renderGraph,
    updateParams,
    renderSceneTree: sceneTree.render,
    renderEditorNavigation: editorNavigation.render,
    syncToolbar: syncViewportControls,
    revealToolbar: revealViewportToolbar,
    scheduleRender,
    // Only a fallback: every resource the bundled documents name is in the map.
    dataSourceBaseUrl: import.meta.url,
    dataSourceUrls: { 'unit-data.json': unitDataSourceUrl },
    projectData,
    setLoadedDataSources: dataRuntime.setLoadedDataSources.bind(dataRuntime),
  }, 'Scene Card');
  eventRuntime.syncSchema(appState.runtimeState);
  await dataSourceRuntime.connectAll();
  evaluateQueries();
  syncRuntimeMode();
  applyStoredViewportRenderMode(appState, editorAppRuntime.setEditorGraphMode, () => getViewportFocusTarget());
  scheduleRender();
  document.documentElement.dataset.appReady = 'true';
}
